import { useEffect, useMemo, useState } from "preact/hooks";
import type { IndexRecord } from "../../shared/snapshot-format";
import type { Character, ManualSpellcastingEntry, SpellRecordFacts } from "../character";
import { addManualEntry, forgetSpell, learnSpell, prepareSpell, removeManualEntry, setSignature, spellCandidates, updateManualEntry } from "../character";
import type { EntryView } from "../character";
import { EMPTY_FILTER, browseEntries, traitFacets, type BrowseAdapter, type EntryFilter } from "../data/browse";
import { loadCatalogue } from "../data/catalogue";
import type { SpellChoiceView, SpellView } from "../engine";
import { ATTRIBUTE_LABELS, rankLabel, signed } from "../engine";
import { EntryFilterBar } from "./library";
import { RollButton } from "./roll-button";
import { useSheetActions } from "./sheet-context";
import { useSearch } from "./use-data";

interface Props {
  character: Character;
  entries: EntryView[];
  busy: boolean;
  /** Why build changes are off (a Session is open), or null. Preparing spells stays on: it is daily. */
  disabled: string | null;
  legacy: boolean;
  onSave: (next: Character) => Promise<void>;
  onMessage: (text: string) => void;
}

const PAGE = 40;
const RANK_KEYS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

function groupLabel(rank: string): string {
  return rank === "0" ? "Cantrips" : `${rankLabel(Number(rank)).replace(" rank", "")} rank`;
}

/**
 * Spells (issue #10): every Spellcasting Entry with its attack and DC, the spells known by rank
 * group, preparations per slot, signature spells, the spell Choices the Entries ask for, and
 * hand-declared Entries. Every change is a pure operation from src/character/spells.ts applied
 * to the Character and saved; the sheet reports what is off-list, the operations refuse only
 * what is structurally impossible. The iPad layout is #14.
 */
export function Spells({ character, entries, busy, disabled, legacy, onSave, onMessage }: Props) {
  const [records, setRecords] = useState<IndexRecord[]>();
  const [editing, setEditing] = useState<string | null>(null);
  useEffect(() => {
    loadCatalogue().then(setRecords, () => setRecords([]));
  }, []);
  const apply = async (result: { character: Character; blocked: string | null }) => {
    if (result.blocked) {
      onMessage(result.blocked);
      return;
    }
    setEditing(null);
    await onSave(result.character);
  };
  const openChoices = entries.reduce((n, e) => n + e.choices.filter((c) => !c.answered).length, 0);

  return (
    <section class="spells">
      <div class="row">
        <h3>
          Spells {openChoices > 0 ? <span class="tag open">{openChoices} open</span> : entries.length > 0 ? <span class="tag">{entries.length === 1 ? "1 Entry" : `${entries.length} Entries`}</span> : null}
        </h3>
        <span class="actions">
          <button type="button" disabled={busy || !!disabled} onClick={() => setEditing(editing === "manual:new" ? null : "manual:new")}>{editing === "manual:new" ? "Close" : "Declare an Entry"}</button>
        </span>
      </div>
      {entries.length === 0 && <p class="muted">No Spellcasting Entry: the Character holds no Casting Source (a caster class, a spellcasting dedication, an Entry granting innate or focus spells). One can be declared by hand.</p>}
      {editing === "manual:new" && (
        <div class="card picker">
          <ManualForm busy={busy} onSubmit={(m) => void apply({ character: addManualEntry(character, m), blocked: null })} onCancel={() => setEditing(null)} />
        </div>
      )}
      {entries.map((entry) => (
        <EntryCard key={entry.id} entry={entry} character={character} records={records ?? []} busy={busy} disabled={disabled} legacy={legacy} editing={editing} setEditing={setEditing} apply={apply} />
      ))}
    </section>
  );
}

interface CardProps {
  entry: EntryView;
  character: Character;
  records: IndexRecord[];
  busy: boolean;
  disabled: string | null;
  legacy: boolean;
  editing: string | null;
  setEditing: (id: string | null) => void;
  apply: (result: { character: Character; blocked: string | null }) => Promise<void>;
}

function EntryCard({ entry, character, records, busy, disabled, legacy, editing, setEditing, apply }: CardProps) {
  const statistic = entry.statistic;
  const manual = character.spellcasting.manual.find((m) => m.id === entry.id);
  const toggle = (id: string) => setEditing(editing === id ? null : id);
  const groups = entry.casting === "prepared" || entry.casting === "spontaneous" || entry.casting === "items" ? RANK_KEYS.filter((r) => entry.slots[r] !== undefined || entry.spells.some((s) => String(s.cantrip ? 0 : s.rank) === r)) : [];
  const spellsAt = (rank: string) => entry.spells.filter((s) => String(s.cantrip ? 0 : entry.casting === "prepared" ? s.baseRank : s.rank) === rank);
  const listed = new Set(groups.flatMap((r) => spellsAt(r).map((s) => `${s.uuid}:${s.rank}`)));
  const unlisted = entry.spells.filter((s) => !listed.has(`${s.uuid}:${s.rank}`));

  return (
    <div class="card entry-card">
      <div class="row">
        <h4>
          {entry.name} <span class="muted">· {entry.traditionLabel} {entry.castingLabel.toLowerCase()} · {ATTRIBUTE_LABELS[entry.attribute]}</span>
        </h4>
        {statistic && (
          <span class="muted">
            attack {signed(statistic.value)} · DC {statistic.dc}{statistic.override ? " (Override)" : ""}{entry.statisticOf ? ` · as ${entry.statisticOf}` : ""}
          </span>
        )}
      </div>
      {entry.source && <p class="muted">From {entry.source.name}{manual ? "" : ""}.</p>}
      {entry.casting === "items" && (
        <p class="muted">
          {entry.charges === null || entry.charges === 0 ? "No charges." : `${entry.charges} charge${entry.charges === 1 ? "" : "s"} at daily preparations`}; a spell costs charges equal to its rank, cantrips cost none. Cast through {entry.statisticOf ? `the ${entry.statisticOf.replace(/^[a-z]+:/, "").replace(/-/g, " ")} Entry's` : "the"} attack and DC.
        </p>
      )}
      {entry.problems.map((p, i) => <p key={i} class="notice">{p}</p>)}
      {entry.choices.length > 0 && (
        <ul class="steps">
          {entry.choices.map((choice) => (
            <ChoiceRow key={choice.key} entry={entry} choice={choice} character={character} records={records} busy={busy} disabled={disabled} legacy={legacy} editing={editing} toggle={toggle} apply={apply} />
          ))}
        </ul>
      )}
      {groups.map((rank) => {
        const slots = entry.slots[rank] ?? 0;
        const known = spellsAt(rank);
        const limit = entry.limits?.[rank];
        const id = `${entry.id}:learn:${rank}`;
        return (
          <div key={rank} class="rank-group">
            <div class="row">
              <strong>{groupLabel(rank)}</strong>
              <span class="muted">
                {entry.casting === "items" ? (rank === "0" ? "free" : `${rank} charge${rank === "1" ? "" : "s"} each`) : entry.casting === "spontaneous" ? (rank === "0" ? `${limit?.used ?? 0} / ${limit?.max ?? 0} cantrips` : `${slots} slot${slots === 1 ? "" : "s"} · ${limit?.used ?? 0} / ${limit?.max ?? 0} known`) : `${slots} slot${slots === 1 ? "" : "s"}`}
              </span>
              <span class="actions">
                {entry.casting === "spontaneous" || (entry.casting === "prepared" && entry.preparesFrom === "spellbook") ? (
                  <button type="button" disabled={busy || !!disabled} onClick={() => toggle(id)}>{editing === id ? "Close" : entry.casting === "spontaneous" ? "Learn" : "Add to spellbook"}</button>
                ) : null}
              </span>
            </div>
            {editing === id && (
              <div class="picker">
                <SpellPicker entry={entry} rank={Number(rank)} records={records} busy={busy} legacy={legacy} onPick={(facts) => void apply(learnSpell(character, entry, facts, entry.casting === "spontaneous" ? { rank: Number(rank), level: character.level } : { level: character.level }))} />
              </div>
            )}
            {entry.casting === "prepared" && slots > 0 && (
              <ul class="plain items slots">
                {(entry.prepared[rank] ?? []).map((slot, index) => {
                  const sid = `${entry.id}:prepare:${rank}:${index}`;
                  return (
                    <li key={index}>
                      <span>
                        <span class="muted">{index + 1}.</span> {slot ? <SpellName spell={slot} entry={entry} /> : <span class="muted">empty</span>}
                      </span>
                      {entry.kind !== "font" && (
                        <span class="actions">
                          {slot && <button type="button" disabled={busy} onClick={() => void apply(prepareSpell(character, entry, Number(rank), index, null))}>Clear</button>}
                          <button type="button" disabled={busy} onClick={() => toggle(sid)}>{editing === sid ? "Close" : "Prepare"}</button>
                        </span>
                      )}
                      {editing === sid && (
                        <div class="picker">
                          <SpellPicker entry={entry} rank={Number(rank)} records={records} busy={busy} legacy={legacy} prepare onPick={(facts) => void apply(prepareSpell(character, entry, Number(rank), index, facts))} />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {known.length > 0 && (entry.casting === "spontaneous" || entry.casting === "items" || entry.preparesFrom === "spellbook") && (
              <ul class="plain items" aria-label={entry.casting === "spontaneous" ? "repertoire" : entry.casting === "items" ? "staff spells" : "spellbook"}>
                <li class="muted">{entry.casting === "spontaneous" ? "Repertoire" : entry.casting === "items" ? "Staff spells" : "Spellbook"}</li>
                {known.map((spell) => (
                  <li key={`${spell.uuid}:${spell.rank}`}>
                    <SpellName spell={spell} entry={entry} />
                    <span class="actions">
                      {entry.casting === "spontaneous" && !spell.cantrip && (
                        <label class="toggle" title="A signature spell can be cast at any rank the Entry has slots for">
                          <input type="checkbox" checked={spell.signature} disabled={busy || !!disabled} onChange={(e) => void apply(setSignature(character, entry, spell.uuid, (e.target as HTMLInputElement).checked))} /> signature
                        </label>
                      )}
                      {!spell.grantedBy && <button type="button" disabled={busy || !!disabled} onClick={() => void apply(forgetSpell(character, entry.id, spell.uuid, entry.casting === "spontaneous" ? spell.rank : undefined))}>Forget</button>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      {(entry.casting === "innate" || entry.casting === "focus" || unlisted.length > 0) && (
        <div class="rank-group">
          <div class="row">
            <strong>{entry.casting === "focus" ? "Focus spells" : entry.casting === "innate" ? "Innate spells" : "Also known"}</strong>
            <span class="actions">
              {(entry.casting === "innate" || entry.casting === "focus") && (
                <button type="button" disabled={busy || !!disabled} onClick={() => toggle(`${entry.id}:add`)}>{editing === `${entry.id}:add` ? "Close" : "Add"}</button>
              )}
            </span>
          </div>
          {editing === `${entry.id}:add` && (
            <div class="picker">
              <SpellPicker entry={entry} rank={null} records={records} busy={busy} legacy={legacy} onPick={(facts) => void apply(learnSpell(character, entry, facts, { level: character.level }))} />
            </div>
          )}
          <ul class="plain items">
            {(entry.casting === "innate" || entry.casting === "focus" ? entry.spells : unlisted).map((spell) => (
              <li key={`${spell.uuid}:${spell.rank}:${spell.grantedBy?.uuid ?? ""}`}>
                <SpellName spell={spell} entry={entry} />
                <span class="actions">
                  {!spell.grantedBy && <button type="button" disabled={busy || !!disabled} onClick={() => void apply(forgetSpell(character, entry.id, spell.uuid))}>Remove</button>}
                </span>
              </li>
            ))}
            {entry.spells.length === 0 && entry.choices.length === 0 && <li class="muted">None yet.</li>}
          </ul>
        </div>
      )}
      {manual && (
        <div class="row">
          <span class="actions">
            <button type="button" disabled={busy || !!disabled} onClick={() => toggle(`${entry.id}:edit`)}>{editing === `${entry.id}:edit` ? "Close" : "Edit Entry"}</button>
            <button type="button" class="danger" disabled={busy || !!disabled} onClick={() => void apply({ character: removeManualEntry(character, entry.id), blocked: null })}>Remove Entry</button>
          </span>
        </div>
      )}
      {manual && editing === `${entry.id}:edit` && (
        <div class="picker">
          <ManualForm busy={busy} initial={manual} onSubmit={(m) => void apply({ character: updateManualEntry(character, entry.id, m), blocked: null })} onCancel={() => setEditing(null)} />
        </div>
      )}
    </div>
  );
}

const SAVE_LABELS: Record<string, string> = { fortitude: "Fortitude", reflex: "Reflex", will: "Will" };

/** The spell's Roll buttons (#19): the spell attack with its MAP variants, the damage at the cast rank, and the save it asks for. */
function SpellRolls({ spell, entry }: { spell: SpellView; entry: EntryView }) {
  const statistic = entry.statistic;
  const damage = spell.damage?.formula.success ? spell.damage : null;
  if (!statistic && !damage && !spell.save) return null;
  return (
    <span class="spell-rolls">
      {spell.attack && statistic && (
        <RollButton class="roll-spell-attack" request={{ kind: "spell-attack", entryId: entry.id, spellUuid: spell.uuid, label: `${spell.name} attack`, map: 0 }}>
          <span class="roll-label">Attack</span>
          <span class="roll-mod">{signed(statistic.value)}</span>
        </RollButton>
      )}
      {damage && (
        <RollButton class="roll-spell-damage" request={{ kind: "spell-damage", entryId: entry.id, spellUuid: spell.uuid, label: `${spell.name} damage` }} title={damage.breakdown.success.join(", ")}>
          <span class="roll-label">{damage.kinds.includes("healing") ? "Heal" : "Damage"}</span>
          <span class="roll-mod">{readableFormula(damage.formula.success)}</span>
        </RollButton>
      )}
      {spell.save && statistic && <span class="tag">{spell.save.basic ? "basic " : ""}{SAVE_LABELS[spell.save.statistic] ?? spell.save.statistic} DC {statistic.dc}</span>}
    </span>
  );
}

/** "{2d6[fire]}" reads "2d6 fire"; "{(2d8 + 4)[slashing],1d6[fire]}" reads "2d8 + 4 slashing, 1d6 fire". */
function readableFormula(formula: string | null): string {
  if (!formula) return "";
  const body = formula.trim().replace(/^\{|\}$/g, "");
  return body.split(/,(?![^[]*\])/).map((part) => part.trim().replace(/^\((.*)\)\[([^\]]*)\]$/, "$1 $2").replace(/\[([^\]]*)\]$/, " $1").replace(/,/g, " ")).join(", ");
}

function SpellName({ spell, entry }: { spell: SpellView; entry?: EntryView }) {
  const { openEntry } = useSheetActions();
  const damage = entry && spell.damage ? null : spell.heightened?.damage.map((d) => `${d.formula} ${d.type}`).join(", ");
  const title = [...(spell.heightened?.changes ?? []), ...spell.problems].join("\n");
  return (
    <span title={title}>
      <button type="button" class="linkish" onClick={() => openEntry(spell.uuid, { rank: spell.rank })}>{spell.name}</button>
      <span class="muted">
        {" "}
        · {spell.cantrip ? "cantrip" : spell.focus ? "focus" : ""} {spell.cantrip || spell.focus ? `heightened to rank ${spell.rank}` : spell.rank !== spell.baseRank ? `rank ${spell.baseRank} at rank ${spell.rank}` : `rank ${spell.rank}`}
        {damage ? ` · ${damage}` : ""}
        {spell.uses !== null && spell.uses !== undefined ? ` · ${spell.uses}/day` : ""}
        {spell.grantedBy ? ` · from ${spell.grantedBy.name}` : ""}
        {!spell.loaded ? " · loading" : ""}
      </span>
      {spell.signature && <span class="tag">signature</span>}
      {spell.remaster === false && <span class="tag legacy">Legacy</span>}
      {spell.heightened && spell.heightened.changes.length > 0 && <span class="tag" title={spell.heightened.changes.join("\n")}>heightened</span>}
      {spell.problems.map((p, i) => <span key={i} class="tag warn">{p}</span>)}
      {entry && <SpellRolls spell={spell} entry={entry} />}
    </span>
  );
}

function ChoiceRow({ entry, choice, character, records, busy, disabled, legacy, editing, toggle, apply }: Omit<CardProps, "setEditing"> & { choice: SpellChoiceView; toggle: (id: string) => void }) {
  const id = `${entry.id}:choice:${choice.key}`;
  const answered = choice.answered ? entry.spells.find((s) => s.uuid === choice.answered) : undefined;
  return (
    <li class={choice.answered ? "" : "open"}>
      <div class="item-row">
        <span class="item-label">{choice.prompt}</span>
        <span class="item-value">
          {answered ? <SpellName spell={answered} entry={entry} /> : <span class="muted">not chosen</span>}
          {!choice.answered && <span class="tag open">open</span>}
        </span>
        <span class="actions">
          {choice.answered ? (
            <button type="button" disabled={busy || !!disabled} onClick={() => void apply(forgetSpell(character, entry.id, choice.answered!))}>Clear</button>
          ) : (
            <button type="button" disabled={busy || !!disabled} onClick={() => toggle(id)}>{editing === id ? "Close" : "Choose"}</button>
          )}
        </span>
      </div>
      {editing === id && (
        <div class="picker">
          <SpellPicker entry={entry} rank={null} choice={choice.key} records={records} busy={busy} legacy={legacy} onPick={(facts) => void apply(learnSpell(character, entry, facts, { choice: choice.key, level: character.level }))} />
        </div>
      )}
    </li>
  );
}

interface PickerProps {
  entry: EntryView;
  rank: number | null;
  choice?: string;
  records: IndexRecord[];
  busy: boolean;
  legacy: boolean;
  /** Preparing: the Entry's spellbook first, then the list. */
  prepare?: boolean;
  onPick: (facts: SpellRecordFacts) => void;
}

/** A search over the spells Pack narrowed to what the Entry (or Choice) may take, with Legacy and rarity as browsing filters. */
const spellAdapter: BrowseAdapter<SpellRecordFacts> = { uuid: (c) => c.uuid, name: (c) => c.name, level: (c) => c.rank, traits: (c) => c.traits, rarity: (c) => c.rarity };

/** The Library's browser as the picker (#15): what the Entry (or Choice) may take, with the browser's search and filters on top. */
function SpellPicker({ entry, rank, choice, records, busy, legacy, prepare, onPick }: PickerProps) {
  const { openEntry } = useSheetActions();
  const [filter, setFilter] = useState<EntryFilter>({ ...EMPTY_FILTER });
  const search = useSearch();
  const [showLegacy, setShowLegacy] = useState(false);
  const [uncommon, setUncommon] = useState(false);
  const [book, setBook] = useState(true);
  const inBook = useMemo(() => new Set(entry.spells.map((s) => s.uuid)), [entry]);
  const candidates = useMemo(() => {
    if (!records.length) return [];
    const all = spellCandidates(records, { entry, rank, choice: choice ?? null, text: "", legacy: legacy || showLegacy, uncommon: uncommon || (prepare === true && entry.preparesFrom === "spellbook"), limit: Number.MAX_SAFE_INTEGER });
    return prepare && book && entry.preparesFrom === "spellbook" ? all.filter((c) => inBook.has(c.uuid)) : all;
  }, [records, entry, rank, choice, legacy, showLegacy, uncommon, prepare, book, inBook]);
  const traits = useMemo(() => traitFacets(candidates, spellAdapter), [candidates]);
  const results = useMemo(() => browseEntries(candidates, filter, search, spellAdapter), [candidates, filter, search]);
  const page = results.slice(0, PAGE);
  const text = filter.text;
  return (
    <div>
      <EntryFilterBar filter={filter} onChange={setFilter} traits={traits} levelLabel="Rank" searchPlaceholder={records.length ? "Search spells" : "Loading the catalogue…"}>
        {!legacy && (
          <label class="toggle">
            <input type="checkbox" checked={showLegacy} onChange={(e) => setShowLegacy((e.target as HTMLInputElement).checked)} /> Legacy
          </label>
        )}
        <label class="toggle">
          <input type="checkbox" checked={uncommon} onChange={(e) => setUncommon((e.target as HTMLInputElement).checked)} /> Uncommon and rarer
        </label>
        {prepare && entry.preparesFrom === "spellbook" && (
          <label class="toggle">
            <input type="checkbox" checked={book} onChange={(e) => setBook((e.target as HTMLInputElement).checked)} /> Spellbook only
          </label>
        )}
      </EntryFilterBar>
      <ul class="candidates">
        {page.map((c) => (
          <li key={c.uuid} class={inBook.has(c.uuid) ? "current" : ""}>
            <button type="button" class="icon info" aria-label={`Read ${c.name}`} onClick={() => openEntry(c.uuid, { rank: rank ?? undefined })}>i</button>
            <button type="button" class="pick" disabled={busy} onClick={() => onPick(c)}>
              <span class="pick-name">{c.name}</span>
              <span class="pick-tags">
                <span class="muted">{c.cantrip ? "cantrip" : c.focus ? "focus" : rankLabel(c.rank)}</span>
                {inBook.has(c.uuid) && <span class="tag">known</span>}
                {c.rarity !== "common" && <span class="tag">{c.rarity}</span>}
                {!c.remaster && <span class="tag legacy">Legacy</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {results.length > page.length && <p class="muted">{results.length - page.length} more; refine the search.</p>}
      {results.length === 0 && records.length > 0 && <p class="muted">No spell fits here{text ? " with that name" : ""}.</p>}
    </div>
  );
}

const TRADITIONS = ["arcane", "divine", "occult", "primal"] as const;
const CASTINGS = ["prepared", "spontaneous", "innate", "focus"] as const;
const ATTRIBUTES = ["str", "dex", "con", "int", "wis", "cha"] as const;

/** Declare or edit a Spellcasting Entry by hand: name, tradition, casting type, attribute and slots per rank. */
function ManualForm({ busy, initial, onSubmit, onCancel }: { busy: boolean; initial?: ManualSpellcastingEntry; onSubmit: (entry: Omit<ManualSpellcastingEntry, "id">) => void; onCancel: () => void }) {
  const [name, setName] = useState(initial?.name ?? "");
  const [tradition, setTradition] = useState<ManualSpellcastingEntry["tradition"]>(initial?.tradition ?? "arcane");
  const [casting, setCasting] = useState<ManualSpellcastingEntry["casting"]>(initial?.casting ?? "spontaneous");
  const [attribute, setAttribute] = useState<ManualSpellcastingEntry["attribute"]>(initial?.attribute ?? "cha");
  const [slots, setSlots] = useState<Record<string, number>>(initial?.slots ?? {});
  const slotted = casting === "prepared" || casting === "spontaneous";
  return (
    <div class="manual-form">
      <div class="inline">
        <input type="text" placeholder="Name (e.g. Eldritch Archer Spells)" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
        <select value={tradition} onChange={(e) => setTradition((e.target as HTMLSelectElement).value as ManualSpellcastingEntry["tradition"])}>
          {TRADITIONS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={casting} onChange={(e) => setCasting((e.target as HTMLSelectElement).value as ManualSpellcastingEntry["casting"])}>
          {CASTINGS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={attribute} onChange={(e) => setAttribute((e.target as HTMLSelectElement).value as ManualSpellcastingEntry["attribute"])}>
          {ATTRIBUTES.map((a) => <option key={a} value={a}>{ATTRIBUTE_LABELS[a]}</option>)}
        </select>
      </div>
      {slotted && (
        <div class="inline slots-form">
          {RANK_KEYS.filter((r) => casting === "prepared" || r !== "0").map((r) => (
            <label key={r}>
              <span class="muted">{r === "0" ? "cantrips" : rankLabel(Number(r)).replace(" rank", "")}</span>
              <input type="number" min="0" max="10" inputMode="numeric" value={slots[r] ?? 0} onInput={(e) => setSlots({ ...slots, [r]: Math.max(0, Number((e.target as HTMLInputElement).value) || 0) })} style="width:3.5rem" />
            </label>
          ))}
        </div>
      )}
      <span class="actions">
        <button type="button" class="primary" disabled={busy || !name.trim()} onClick={() => onSubmit({ name: name.trim(), tradition, casting, attribute, slots: slotted ? Object.fromEntries(Object.entries(slots).filter(([, n]) => n > 0)) : {} })}>{initial ? "Save" : "Declare"}</button>
        <button type="button" disabled={busy} onClick={onCancel}>Cancel</button>
      </span>
    </div>
  );
}
